import React, { useEffect, useRef, useState } from 'react';
import './PagePreview.css';

export interface PagePreviewProps {
  /** Stable URL pointing at the JPEG thumbnail for one page (built by
   *  the consumer via ``documentService.getPageThumbnailUrl``). When
   *  ``null`` the tile shows its loading skeleton without making a
   *  request — used while the document metadata hasn't resolved yet. */
  src: string | null;
  /** 1-indexed page number this tile represents. Surfaced via
   *  ``aria-label`` for screen readers and passed back through
   *  ``onClick`` so the consumer can deep-link the modal. */
  page: number;
  /** Click handler — fires with the tile's ``page`` so consumers don't
   *  need to thread an extra argument through. */
  onClick?: (page: number) => void;
  /** "+N more" variant — shows the count overlay instead of an image
   *  and fires ``onClick`` with the ``page`` prop (typically the first
   *  page beyond the rendered tiles). */
  overflowCount?: number;
  /** A11y label, e.g. translated "Vorschau Seite 1". */
  ariaLabel?: string;
  /** Pixel width hint — passed through to the IntersectionObserver
   *  threshold and the wrapper sizing so the layout doesn't shift when
   *  the image resolves. */
  width?: number;
}

/** Page-thumbnail tile shown in the expanded ``<Document>`` card
 *  (issue #755). Lazy-loads via IntersectionObserver so cards below
 *  the fold don't fetch JPEGs until they scroll into view.
 *
 *  Renders three visual states:
 *   - skeleton (loading / pre-intersect / ``src=null``)
 *   - JPEG thumbnail (on success)
 *   - error placeholder (img onError; e.g. backend returned 404 for a
 *     page that doesn't exist in the stub PDF from #767)
 */
export const PagePreview: React.FC<PagePreviewProps> = ({
  src,
  page,
  onClick,
  overflowCount,
  ariaLabel,
  width = 120,
}) => {
  const wrapperRef = useRef<HTMLButtonElement>(null);
  const [inView, setInView] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      // jsdom test envs and very old browsers — skip the lazy gate.
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView]);

  const handleClick = () => {
    if (onClick) onClick(page);
  };

  const label =
    overflowCount !== undefined
      ? `+${overflowCount} ${overflowCount === 1 ? 'page' : 'more'}`
      : null;

  return (
    <button
      ref={wrapperRef}
      type="button"
      className={`page-preview${overflowCount !== undefined ? ' page-preview--overflow' : ''}`}
      style={{ width, height: Math.round(width * 1.41) }}
      onClick={handleClick}
      aria-label={ariaLabel ?? (overflowCount !== undefined ? label ?? '' : `Page ${page}`)}
    >
      {overflowCount !== undefined ? (
        <span className="page-preview-overflow-label">{label}</span>
      ) : !src || !inView ? (
        <span className="page-preview-skeleton" aria-hidden="true" />
      ) : errored ? (
        <span className="page-preview-error" aria-hidden="true">
          —
        </span>
      ) : (
        <img
          className="page-preview-img"
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
        />
      )}
    </button>
  );
};

export default PagePreview;
